import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Truck, MapPin, ArrowRight, Tag, Heart } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePricing } from '@/hooks/usePricing';
import { profileApi } from '@/services/api';
import { trackProductClick } from '@/utils/behaviorTracking';
import { productCardImageUrl } from '@/utils/productCardImageUrl';
import { toast } from 'sonner';

const FavoriteButton = ({ productId }) => {
  const { isAuthenticated } = useAuth();
  const [isFav, setIsFav] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !productId) return;
    profileApi.checkFavorite(productId)
      .then(res => setIsFav(res.data.is_favorite))
      .catch(() => {});
  }, [isAuthenticated, productId]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.info('Logga in för att spara favoriter');
      return;
    }
    if (loading) return;
    setLoading(true);
    try {
      if (isFav) {
        await profileApi.removeFavorite(productId);
        setIsFav(false);
      } else {
        await profileApi.addFavorite(productId);
        setIsFav(true);
      }
    } catch {
      toast.error('Kunde inte uppdatera favorit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={toggle}
      className={`absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm ${
        isFav
          ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 scale-100'
          : 'bg-white/80 text-gray-400 hover:bg-white hover:text-red-500 shadow-sm'
      }`}
      aria-label={isFav ? 'Ta bort favorit' : 'Lägg till favorit'}
      data-testid={`favorite-btn-${productId}`}
    >
      <Heart className={`h-4 w-4 ${isFav ? 'fill-current' : ''}`} />
    </button>
  );
};

export const ProductCard = ({ product, hideShipping = false }) => {
  const { 
    id, 
    title, 
    price, 
    original_price,
    has_campaign,
    discount_amount,
    discount_percent,
    shipping_cost, 
    condition,
    store_name,
    county,
    images 
  } = product;
  
  const { displayPrice, vatLabel, isBusiness } = usePricing();
  const imageUrl = productCardImageUrl(
    images?.[0] || 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1200&q=85',
  );
  const savings = discount_amount || (original_price ? Math.round(original_price - price) : 0);
  const conditionLabel = condition === 'demo' ? 'Demoex' : condition === 'defect' ? 'Defekt' : 'Ny vara';
  const conditionClass = condition === 'demo'
    ? 'bg-amber-100 text-amber-700'
    : condition === 'defect'
      ? 'bg-rose-100 text-rose-700'
      : 'bg-emerald-100 text-emerald-700';

  return (
    <Link 
      to={`/produkt/${id}`} 
      className="group relative flex flex-col bg-white rounded-[10px] overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1.5" 
      style={{ 
        boxShadow: '0 2px 8px -2px rgba(0,0,0,0.08), 0 4px 16px -4px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.04)'
      }}
      onClick={() => trackProductClick(id)}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 12px 32px -8px rgba(0,0,0,0.15), 0 4px 16px -4px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 8px -2px rgba(0,0,0,0.08), 0 4px 16px -4px rgba(0,0,0,0.06)';
      }}
      data-testid={`product-card-${id}`}
    >
      {/* Rabatt-badge */}
      {has_campaign && discount_percent > 0 && (
        <div className="absolute top-3 left-3 z-10">
          <span 
            className="inline-flex items-center gap-1 text-white text-[10px] font-bold px-2 py-1 rounded-md"
            style={{ 
              background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
              boxShadow: '0 2px 8px -2px rgba(220, 38, 38, 0.5)'
            }}
          >
            <Tag className="h-2.5 w-2.5" />
            -{discount_percent}%
          </span>
        </div>
      )}
      
      {/* Favorit-hjärta */}
      <FavoriteButton productId={id} />
      
      {/* Produktbild */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
        <img
          src={imageUrl}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      
      {/* Content */}
      <div className="flex flex-col flex-grow p-4 md:p-5">
        <h3 
          className="text-[#0B1E33] text-sm md:text-[15px] leading-snug line-clamp-2 mb-3 group-hover:text-[#1E3A5F] transition-colors duration-200"
          style={{ 
            fontFamily: 'var(--font-heading)',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            minHeight: '2.6rem'
          }}
        >
          {title}
        </h3>

        <div className="mb-3">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${conditionClass}`}>
            {conditionLabel}
          </span>
        </div>
        
        {/* Pris */}
        <div className="mb-3">
          {has_campaign && original_price ? (
            <div className="space-y-1">
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <span 
                  className="text-[22px] md:text-[26px] text-[#DC2626]"
                  style={{ fontFamily: 'var(--font-heading)', fontWeight: 900, letterSpacing: '-0.02em' }}
                >
                  {displayPrice(price).toLocaleString('sv-SE')} <span className="text-base">kr</span>
                </span>
                <span className="text-[13px] text-slate-400 line-through font-normal">
                  {displayPrice(original_price).toLocaleString('sv-SE')} kr
                </span>
              </div>
              <p className="text-[11px] font-medium text-emerald-600 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                Du sparar {displayPrice(savings).toLocaleString('sv-SE')} kr
              </p>
              <p className="text-[10px] text-gray-400">{vatLabel}</p>
            </div>
          ) : (
            <div>
              <p 
                className="text-[22px] md:text-[26px] text-[#0B1E33]"
                style={{ fontFamily: 'var(--font-heading)', fontWeight: 900, letterSpacing: '-0.02em' }}
              >
                {displayPrice(price)?.toLocaleString('sv-SE')} <span className="text-base font-bold">kr</span>
              </p>
              <p className="text-[10px] text-gray-400">{vatLabel}</p>
            </div>
          )}
        </div>
        
        {/* Frakt */}
        {!hideShipping && shipping_cost !== undefined && (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-3">
            <Truck className="h-3 w-3 text-slate-400" />
            <span>{shipping_cost > 0 ? `Frakt från ${shipping_cost} kr` : 'Fri frakt'}</span>
          </div>
        )}
        
        <div className="flex-grow" />
        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-3" />
        
        {/* Butik & Visa produkt */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 truncate max-w-[55%]">
            <MapPin className="h-3 w-3 text-slate-400 flex-shrink-0" />
            <span className="truncate">{store_name || 'ByggFynd'}</span>
          </div>
          <span 
            className="inline-flex items-center gap-1 text-[12px] font-bold text-[#F97316] group-hover:text-[#EA580C] transition-all duration-200 whitespace-nowrap group-hover:gap-1.5"
          >
            Visa produkt
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};
